import { useEffect, useId, useRef, type ReactNode } from "react"
import { motion, useIsPresent } from "motion/react"
import { cx } from "@/lib/utils/cx"

// Shared pieces for every non-camera screen of the consumer flow. The frame
// itself (Back, shop chip, the one progress bar) lives in flow-frame.tsx.

// Move screen-reader + keyboard context to the new screen's heading whenever
// the phase changes. Steps tag their h1/h2 with <FlowHeading>.
// Each new screen starts at the top (under the sticky frame, never behind it).
// Focus moves with the screen: FlowHeading focuses itself when it mounts —
// after the old screen's exit animation, so it's never a heading on its way out.
export function useFlowHeadingFocus(key: string) {
  useEffect(() => {
    const raf = requestAnimationFrame(() => window.scrollTo({ top: 0 }))
    return () => cancelAnimationFrame(raf)
  }, [key])
}

export function FlowHeading({
  children,
  className,
  as: Tag = "h1",
}: {
  children: ReactNode
  className?: string
  as?: "h1" | "h2"
}) {
  const ref = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    const raf = requestAnimationFrame(() => ref.current?.focus({ preventScroll: true }))
    return () => cancelAnimationFrame(raf)
  }, [])
  return (
    <Tag ref={ref} data-flow-heading tabIndex={-1} className={cx("outline-none focus-visible:shadow-none", className)}>
      {children}
    </Tag>
  )
}

// Standard phase transition wrapper — one spring, both directions. A screen
// on its way out ignores taps, so a quick double-tap can never fire a button
// on the screen that's leaving.
export function PhasePane({ children, className }: { children: ReactNode; className?: string }) {
  const present = useIsPresent()
  return (
    <motion.div
      style={present ? undefined : { pointerEvents: "none" }}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ type: "spring", stiffness: 260, damping: 30, mass: 0.9 }}
      className={cx("flex flex-1 flex-col", className)}
    >
      {children}
    </motion.div>
  )
}

// The flow's primary CTA — thumb-height (h-14), full-width, in-flow (never
// fixed) so the keyboard can't cover it on phones.
export function FlowCta({
  children,
  onClick,
  disabled,
  loading,
  type = "button",
  className,
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  loading?: boolean
  type?: "button" | "submit"
  className?: string
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={cx(
        "flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-zinc-900 text-[15.5px] font-semibold text-white shadow-lg shadow-zinc-900/20 transition-all duration-150",
        "hover:bg-black hover:shadow-zinc-900/30",
        "disabled:cursor-not-allowed disabled:bg-zinc-300 disabled:text-zinc-500 disabled:shadow-none",
        className,
      )}
    >
      {loading && (
        <span
          className="size-4 animate-spin rounded-full border-2 border-white/30 border-t-white"
          aria-hidden
        />
      )}
      {children}
    </button>
  )
}

// The primary action stays in reach at the bottom of the screen while the
// content scrolls under it (screens without a keyboard: zones, brief, review).
// Thumb reach on phones: the bar sits at the bottom of the screen. On a big
// screen with a mouse it follows the content instead of floating far below.
export function StickyActions({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cx(
        "sticky bottom-0 z-10 -mx-4 mt-auto bg-gradient-to-t from-paper from-75% to-paper/0 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-6 md:-mx-6 md:px-6 lg:pointer-fine:mt-4",
        className,
      )}
    >
      {children}
    </div>
  )
}

// Quiet secondary action under a CTA ("Skip", "I'll wait to hear from them").
export function QuietLink({
  children,
  onClick,
  disabled,
  className,
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "mx-auto flex h-11 items-center justify-center gap-2 rounded-xl px-3 text-[14px] font-semibold text-zinc-600 transition-colors duration-150 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:opacity-50",
        className,
      )}
    >
      {children}
    </button>
  )
}

// A labelled form field: the label is a real <label> tied to the control, and
// the hint and error are read out with it.
export function IntakeField({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string | false
  children: (control: { id: string; "aria-describedby"?: string }) => ReactNode
}) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(" ") || undefined
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <label id={`${id}-label`} htmlFor={id} className="text-[14px] font-semibold text-ink">
          {label}
        </label>
        {hint && (
          <span id={hintId} className="text-[13px] leading-tight text-zinc-500">
            {hint}
          </span>
        )}
      </div>
      {children({ id, "aria-describedby": describedBy })}
      {error && (
        <p id={errorId} className="mt-1.5 text-[14px] font-medium text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

// One row of a summary list (<dl>): an icon standing in for the term, the
// value in two lines.
export function DetailRow({
  icon,
  label,
  title,
  body,
}: {
  icon: ReactNode
  label: string
  title: string
  body?: string
}) {
  return (
    <div className="flex items-center gap-3 py-4">
      <dt className="shrink-0 text-zinc-500">
        {icon}
        <span className="sr-only">{label}</span>
      </dt>
      <dd className="min-w-0">
        <span className="block text-[16px] font-semibold leading-tight text-ink">{title}</span>
        {body && <span className="mt-1 block text-[14px] leading-tight text-zinc-600">{body}</span>}
      </dd>
    </div>
  )
}
